/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { v4 } from "uuid";
import { StateContext } from "../../../../context/globalContext/context";
import { iGlobalContext } from "../../../../context/globalContext/interface";
import { useHistoryContext } from "../../../../context/historyContext/historyContext";
import { iHistoryContext } from "../../../../context/historyContext/interface";
import { getAccessTokenAPI } from "../../../../api/config/getAccessToken";
import { getSingleHistoryAPICall } from "../../../../api/history/getHistory";
import { useNavigationUrl } from "../../../../hooks/useNavigationUrl";
import { ROUTES } from "../../../../constant";
import { ScreenTypes } from "../../../../enums";
import { isValidJWT } from "../../../../utils/authentication/isValidJWT";
import { consoleLog } from "../../../../utils/consoleLog/consoleLog";
import { v2orch } from "../../../../utils/v2OrchLog";
import { withChatTitle } from "../../../../utils/chat/chatTitle";

export const useHistorySync = () => {
  const {
    userInfo,
    accessJWT,
    setChatData,
    setOnBoardingStep,
    isMobile,
    setFirstScreen,
    setScreen,
    chatData,
    isValidAccessToken,
  }: iGlobalContext | any = useContext(StateContext); // Added any because getAccessToken might not be in iGlobalContext based on App.tsx usage

  const {
    getHistory,
    callSaveHistoryAPIFlag,
    saveHistory,
    setCallSaveHistoryAPIFlag,
    setCurrentHistoryId,
  }: iHistoryContext = useHistoryContext();

  const navigate = useNavigate();
  const { constructNavigationUrl } = useNavigationUrl();

  async function saveAndGetHistoryUserOpenFromMainWebsite(chatData: any) {
    await saveHistory(chatData, "POST");
    if (!isMobile) {
      await getHistory(true);
    }
  }

  async function getSingleHistoryDetail(data: any) {
    setOnBoardingStep("singUp");
    const formattedData = decodeURIComponent(data)?.replace(/\+/g, " ").replace(/=/g, "").replace(/\?/g, "");
    let tempAccessToken = { data: accessJWT };
    if (!userInfo?.loggedIn) tempAccessToken = await getAccessTokenAPI();
    const response = await getSingleHistoryAPICall(tempAccessToken?.data, formattedData);
    if (response) {
      const tempChatData = withChatTitle(
        response?.history?.map((ele: any) =>
          ele?.isUserMessage ? { ...ele, id: v4() } : { ...ele, isSaved: false, isBooked: false, id: v4() }
        ),
        response?.title
      );
      // Spread-then-assign-id preserves persisted `agents` / `router` /
      // `traceId` on each hydrated entry (only `id` is overridden, plus
      // `isSaved` / `isBooked` on assistant entries). Counts feed the
      // verification log so a tester can confirm orchestrator trace
      // round-trips from save → hydrate.
      if (Array.isArray(tempChatData)) {
        const withAgents = tempChatData.filter((e: any) => Array.isArray(e?.agents) && e.agents.length > 0);
        const agentKeyDistribution: Record<string, number> = {};
        for (const e of withAgents) {
          const key = e?.agents?.[0]?.key;
          if (typeof key === "string") {
            agentKeyDistribution[key] = (agentKeyDistribution[key] ?? 0) + 1;
          }
        }
        v2orch("history.hydrate", {
          entries: tempChatData.length,
          withAgents: withAgents.length,
          withoutAgents: tempChatData.length - withAgents.length,
          agentKeyDistribution,
        });
      }
      if (!tempChatData || tempChatData?.length === 0) {
        if (isMobile) {
          const navigationUrl = constructNavigationUrl(ROUTES.Home);
          navigate(navigationUrl);
        } else {
          setFirstScreen("");
          setScreen("");
        }
        return;
      }
      setChatData(tempChatData);
      setCurrentHistoryId(response?.id);
      saveAndGetHistoryUserOpenFromMainWebsite(tempChatData);
      if (isMobile) {
        const navigationUrl = constructNavigationUrl(ROUTES.TopDestination);
        navigate(navigationUrl);
      } else {
        setFirstScreen(ScreenTypes.topDestination);
      }
    } else {
      if (isMobile) {
        const navigationUrl = constructNavigationUrl(ROUTES.Home);
        navigate(navigationUrl);
      } else {
        setFirstScreen("");
        setScreen("");
      }
    }
  }

  // Dedupe concurrent re-fires of the save effect. Its deps include accessJWT /
  // isValidAccessToken, so a token refresh that changes accessJWT WHILE a save is
  // in flight (flag still true, not yet reset) re-runs the effect and fires a
  // SECOND save with the same flag/method → a duplicate POST row for one turn.
  const savingRef = useRef(false);
  const pendingResaveRef = useRef(false);
  const chatDataRef = useRef(chatData);
  chatDataRef.current = chatData;

  /**
   * save chat content to History
   */
  async function saveAndGetHistory() {
    if (savingRef.current) {
      if (callSaveHistoryAPIFlag.flag) pendingResaveRef.current = true;
      return;
    }
    if (callSaveHistoryAPIFlag.flag && chatDataRef.current && chatDataRef.current?.length > 0 && userInfo?.id) {
      savingRef.current = true;
      try {
        await saveHistory(chatDataRef.current, callSaveHistoryAPIFlag.method);
        if (!isMobile) {
          await getHistory(true);
        }
      } finally {
        savingRef.current = false;
      }
      if (pendingResaveRef.current) {
        pendingResaveRef.current = false;
        setCallSaveHistoryAPIFlag({ flag: true, method: "PUT" });
        return;
      }
    }
    if (callSaveHistoryAPIFlag.flag) {
      setCallSaveHistoryAPIFlag({
        flag: false,
        method: "",
      });
    }
  }

  useEffect(() => {
    if (isValidJWT(accessJWT)) saveAndGetHistory();
  }, [callSaveHistoryAPIFlag, isValidAccessToken, accessJWT]);

  useEffect(() => {
    if (isValidJWT(accessJWT)) getHistory();
  }, [isValidAccessToken, accessJWT]);

  return { getSingleHistoryDetail };
};
